import React, { useCallback, useEffect, useState } from 'react';

import { Flex, RadioGroup } from '@chakra-ui/react';
import { Box, Grid } from '@material-ui/core';
import RichTextEditor from 'components/RichTextEditor';
import SkeletonLoader from 'components/SkeletonLoader';
import ReviewQuestion from 'models/reviewQuestion';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { StyledBox, StyledGrid, StyledTypography } from 'styles/common';

import { REVIEW_ANSWER_TYPE_MAP, REVIEW_QUESTION_TYPES, REVIEW_TEXTS } from 'constants/ProjectConstants';
import theme from 'constants/theme';

import { getQAForm, updateQuestionResponse } from 'services/performanceReview';
import { showErrorSnackbar, showInfoSnackbar } from 'services/snackbar';
import { getReviewTemplateQuestions } from 'services/templates/review';

import NumberRating from 'uiToolkit/NumberRating';
import StarRating from 'uiToolkit/StarRating';
import { Typography18 } from 'uiToolkit/Typography';
import { Radio } from 'uiToolkitV1/Radio';

import { useReview } from '../ReviewContext';
import { ControlledTextField, DropDown, RatingSelect, MultiDropDown, DefaultAnswerComp } from './AnswerComponents';
import DisplayInfo from './DisplayInfo';
import { getTemplatePreviewQuestion } from './GoalForm';
import PeerFeedbackComp from './PeerFeedbackComp';

const StickyGridContainer = styled(Grid)`
  min-height: 40vh;
  max-height: 100vh;
  position: sticky;
  top: 9vh;
  overflow-y: auto;
`;

const RatingContainer = styled(StyledGrid)`
  background: ${(props) => props.theme.palette.background.lightGrey};
`;

const Question = ({ children }) => (
  <StyledTypography variant="body1" bottomGutter={2}>
    {children}
  </StyledTypography>
);

const Answer = ({ question, submitAnswer, isSubmitted, isSubmitClicked, isManagerView }) => {
  const {
    id,
    answerType,
    enableFeedbackText,
    answerOptions,
    selectedAnswerOptions: alreadyAnswedOptions,
    feedbackText = '',
    feedbackTextHtml = '',
    loading,
    isRequired,
    isTextRequired,
    minOptions,
    maxOptions,
    isViewOnlyModeEnabled,
  } = question;
  const selectedAnswerOptions = alreadyAnswedOptions || [];

  if (isViewOnlyModeEnabled) {
    return <span>-</span>;
  }
  switch (answerType) {
    case 'DROP_DOWN':
      return (
        <StyledGrid container>
          <StyledGrid item xs={6} style={{ paddingRight: '0.75rem' }}>
            {isSubmitted ? (
              <div>
                <Typography18 color="textprimary">
                  {selectedAnswerOptions?.length
                    ? selectedAnswerOptions?.map((ans) => ans.description).join(', ')
                    : 'NA'}
                </Typography18>
              </div>
            ) : (
              <DropDown
                label={`Select ${isRequired ? '*' : ''}`}
                answerOptions={answerOptions}
                selectedAnswerOptions={selectedAnswerOptions}
                submitAnswer={(field, answer) =>
                  submitAnswer({
                    questionId: id,
                    field,
                    answer,
                  })
                }
                loading={loading}
                width="100%"
                isSubmitted={isSubmitted}
                isRequired={(!selectedAnswerOptions || !selectedAnswerOptions[0]) && isRequired && isSubmitClicked}
                isMandatory={isRequired}
              />
            )}
          </StyledGrid>
          {enableFeedbackText && (
            <StyledGrid item xs={6} style={{ paddingLeft: '0.75rem' }}>
              {isSubmitted ? (
                <div>
                  <RichTextEditor
                    placeholder="NA"
                    initialText={feedbackTextHtml}
                    plainText={feedbackText}
                    showToolbar={false}
                    readOnly
                  />
                </div>
              ) : (
                <ControlledTextField
                  defaultText={feedbackTextHtml || ''}
                  plainDefaulText={feedbackText || ''}
                  placeholder={`Please elaborate with examples. ${isRequired && isTextRequired ? '*' : ''}`}
                  onSubmit={(htmlText, plainText) =>
                    submitAnswer({
                      questionId: id,
                      field: 'feedbackText',
                      answer: { htmlText, plainText },
                    })
                  }
                  loading={loading}
                  disabled={isSubmitted}
                  isRequired={isRequired && isTextRequired && isSubmitClicked}
                />
              )}
            </StyledGrid>
          )}
        </StyledGrid>
      );

    case 'MULTI_SELECT':
      return (
        <StyledGrid container>
          <StyledGrid item xs={6} style={{ paddingRight: '0.75rem' }}>
            {isSubmitted ? (
              <div>
                <Typography18 color="textprimary">
                  {selectedAnswerOptions?.length
                    ? selectedAnswerOptions?.map((ans) => ans.description).join(', ')
                    : 'NA'}
                </Typography18>
              </div>
            ) : (
              <MultiDropDown
                answerOptions={answerOptions}
                selectedAnswerOptions={selectedAnswerOptions}
                submitAnswer={(field, answer) =>
                  submitAnswer({
                    questionId: id,
                    field,
                    answer,
                  })
                }
                minOptions={minOptions}
                maxOptions={maxOptions}
                loading={loading}
                width="100%"
                isSubmitted={isSubmitted}
                isRequired={isRequired}
                isSubmitClicked={isSubmitClicked}
              />
            )}
          </StyledGrid>
          {enableFeedbackText && (
            <StyledGrid item xs={6} style={{ paddingLeft: '0.75rem' }}>
              {isSubmitted ? (
                <div>
                  <RichTextEditor
                    placeholder="-"
                    initialText={feedbackTextHtml}
                    plainText={feedbackText}
                    showToolbar={false}
                    readOnly
                  />
                </div>
              ) : (
                <ControlledTextField
                  defaultText={feedbackTextHtml || ''}
                  plainDefaulText={feedbackText || ''}
                  placeholder={`Please elaborate with examples. ${isRequired && isTextRequired ? '*' : ''}`}
                  onSubmit={(htmlText, plainText) =>
                    submitAnswer({
                      questionId: id,
                      field: 'feedbackText',
                      answer: { htmlText, plainText },
                    })
                  }
                  loading={loading}
                  disabled={isSubmitted}
                  isRequired={isRequired && isTextRequired && isSubmitClicked}
                />
              )}
            </StyledGrid>
          )}
        </StyledGrid>
      );

    case 'LIST_SELECT':
      return (
        <RatingSelect
          answerOptions={answerOptions}
          selectedAnswerOptions={selectedAnswerOptions}
          submitAnswer={(answer) =>
            submitAnswer({
              questionId: id,
              field: 'rating',
              answer,
            })
          }
          loading={loading}
          isSubmitted={isSubmitted}
          isRequired={isRequired && isSubmitClicked}
        />
      );
    case REVIEW_ANSWER_TYPE_MAP.STAR_RATING:
      return (
        <StyledBox flexcontainer justifycontent="flex-start" alignitems="flex-start" marginLeft="-16px">
          <StarRating
            onChange={(newValue) => {
              submitAnswer({
                questionId: id,
                field: 'rating',
                answer: answerOptions?.filter((ans) => ans.displayOrder === newValue),
              });
            }}
            max={answerOptions?.length}
            prevVal={selectedAnswerOptions[0]?.displayOrder || 0}
            size={isManagerView ? 'smallest' : 'small'}
            readOnly={isSubmitted}
          />
          {!isSubmitted && (
            <StyledBox flexcontainer justifycontent="flex-start" alignitems="flex-start">
              {isRequired ? (
                <StyledTypography
                  color={isSubmitClicked && !selectedAnswerOptions?.length ? 'red' : 'textPrimary'}
                  weight={isSubmitClicked && !selectedAnswerOptions?.length ? 'bold' : 'normal'}
                >
                  *
                </StyledTypography>
              ) : (
                ''
              )}
            </StyledBox>
          )}
          {enableFeedbackText && (
            <StyledGrid item xs={6} style={{ paddingLeft: '0.75rem' }}>
              {isSubmitted ? (
                <div>
                  <RichTextEditor
                    placeholder="NA"
                    initialText={feedbackTextHtml}
                    plainText={feedbackText}
                    showToolbar={false}
                    readOnly
                  />
                </div>
              ) : (
                <ControlledTextField
                  defaultText={feedbackTextHtml || ''}
                  plainDefaulText={feedbackText || ''}
                  placeholder={`Please elaborate with examples. ${isRequired && isTextRequired ? '*' : ' '}`}
                  onSubmit={(htmlText, plainText) =>
                    submitAnswer({
                      questionId: id,
                      field: 'feedbackText',
                      answer: { htmlText, plainText },
                    })
                  }
                  loading={loading}
                  disabled={isSubmitted}
                  isRequired={isRequired && isTextRequired && isSubmitClicked}
                />
              )}
            </StyledGrid>
          )}
        </StyledBox>
      );
    case REVIEW_ANSWER_TYPE_MAP.NUMBER_RATING:
      return (
        <StyledBox flexcontainer justifycontent="flex-start" alignitems="flex-start">
          <NumberRating
            data={answerOptions}
            onSelect={(selectedId) => {
              submitAnswer({
                questionId: id,
                field: 'rating',
                answer: answerOptions.filter((ans) => ans.id === selectedId),
              });
            }}
            selectedId={selectedAnswerOptions[0]?.id || null}
            descriptionKey="score"
            error={isRequired && isSubmitClicked && !selectedAnswerOptions?.length}
            readOnly={isSubmitted}
          />
          {!isSubmitted && (
            <StyledBox flexcontainer justifycontent="flex-start" alignitems="flex-start">
              {isRequired ? '*' : ''}
            </StyledBox>
          )}
          {enableFeedbackText && (
            <StyledGrid item xs={6} style={{ paddingLeft: '0.75rem' }}>
              {isSubmitted ? (
                <div>
                  <RichTextEditor
                    placeholder="NA"
                    initialText={feedbackTextHtml}
                    plainText={feedbackText}
                    showToolbar={false}
                    readOnly
                  />
                </div>
              ) : (
                <ControlledTextField
                  defaultText={feedbackTextHtml || ''}
                  plainDefaulText={feedbackText || ''}
                  placeholder={`Please elaborate with examples. ${isRequired && isTextRequired ? '*' : ''}`}
                  onSubmit={(htmlText, plainText) =>
                    submitAnswer({
                      questionId: id,
                      field: 'feedbackText',
                      answer: { htmlText, plainText },
                    })
                  }
                  loading={loading}
                  disabled={isSubmitted}
                  isRequired={isRequired && isTextRequired && isSubmitClicked}
                />
              )}
            </StyledGrid>
          )}
        </StyledBox>
      );
    default: {
      return (
        <>
          {loading && <SkeletonLoader skeletons={['60%']} />}
          {!loading &&
            (isSubmitted ? (
              <div>
                <RichTextEditor
                  placeholder="NA"
                  initialText={feedbackTextHtml}
                  plainText={feedbackText}
                  showToolbar={false}
                  readOnly
                />
              </div>
            ) : (
              <DefaultAnswerComp
                loading={loading}
                disabled={isSubmitted}
                placeholder={`Please elaborate with examples. ${isRequired && isTextRequired ? '*' : ''}`}
                defaultText={feedbackTextHtml || ''}
                plainDefaulText={feedbackText || ''}
                showError={isRequired && isTextRequired && !feedbackText && isSubmitClicked}
                onSubmit={(htmlText, plainText) =>
                  submitAnswer({
                    questionId: id,
                    field: 'feedbackText',
                    answer: { htmlText, plainText },
                  })
                }
              />
            ))}
        </>
      );
    }
  }
};

const QAComponent = ({
  question,
  questionNo,
  submitAnswer,
  isSubmitClicked,
  isManagerView,
  isPreview,
  previewTemplateView,
}) => {
  const { description, hasCustomDescription, providerToDescription } = question;

  const {
    state: { peerFeedback, selectedReviewInfo },
    showPeerFeedback,
    clearPeerFeedback,
  } = useReview();

  return (
    <StyledBox
      padding="1rem 1.5rem"
      onClick={() => {
        if (peerFeedback.questionId && peerFeedback.questionId === question.id) clearPeerFeedback();
        else
          showPeerFeedback({
            questionId: question.id,
            answerType: question.answerType,
            answerOptions: question.answerOptions,
          });
      }}
      bgcolor={
        isManagerView && question.id === peerFeedback.questionId ? theme.palette.background.lightBlue : 'transparent'
      }
    >
      {hasCustomDescription && providerToDescription ? (
        <StyledTypography wordBreak variant="body2" color="textPrimary">
          {getTemplatePreviewQuestion(previewTemplateView, providerToDescription)}
        </StyledTypography>
      ) : (
        <Question>{`${questionNo}. ${description}`}</Question>
      )}

      <Answer
        question={question}
        submitAnswer={submitAnswer}
        isSubmitted={isPreview ? false : selectedReviewInfo.isSubmitted === null || selectedReviewInfo.isSubmitted}
        isSubmitClicked={isSubmitClicked}
        isManagerView={isManagerView}
      />
    </StyledBox>
  );
};

const QAForm = ({
  reviewId,
  employeeId,
  providerId,
  isManagerView,
  isSummaryView = false,
  isSubmitClicked,
  responseId,
  refetchForm,
  isPreview,
  isShowQNARating,
}) => {
  const [loading, setLoading] = useState(false);
  const [questionList, setQuestionList] = useState(null);
  const { showPeerFeedback } = useReview();
  const [qnaRating, setQNARating] = useState(null);
  const [previewTemplateView, setPreviewTemplateView] = useState('SELF');

  const dispatch = useDispatch();

  const { templateId } = useParams();

  const fetchData = () => {
    setLoading(true);
    if (isPreview) {
      getReviewTemplateQuestions({
        templateId,
        section: REVIEW_QUESTION_TYPES.QNA,
      })
        .then((res) => {
          setQuestionList(
            res.qnaQuestions.map((templateQuestion) => ({
              ...ReviewQuestion.fromJSON(templateQuestion.question),
              loading: false,
            }))
          );
        })
        .catch((err) => console.error(err))
        .finally(() => setLoading(false));
    } else {
      getQAForm({
        reviewId,
        employeeId,
        providerId,
        responseId,
      })
        .then(({ QNAQuestions, averageQNARating, maxQNARating }) => {
          setQuestionList(QNAQuestions.map((question) => ({ ...question, loading: false })));
          setQNARating({
            averageQNARating,
            maxQNARating,
          });
          try {
            showPeerFeedback({
              questionId: QNAQuestions[0].id,
              answerType: QNAQuestions[0].answerType,
              answerOptions: QNAQuestions[0].answerOptions,
            });
          } catch (e) {
            console.error(e);
          }
        })
        .catch((err) => {
          console.log(err);
          dispatch(showErrorSnackbar(err.message || 'Some Error Occured while fetching QA Form'));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const answerQuestion = useCallback(({ questionId, field, answer }) => {
    let currentFeedBackText = '';
    let currentFeedBackTextHtml = '';
    let currentSelectedAnswerOptions = [];
    // loader added below
    setQuestionList((questions) => {
      if (Array.isArray(questions))
        return questions.map((question) => {
          if (question.id !== questionId) return question;

          currentFeedBackText = question.feedbackText;
          currentFeedBackTextHtml = question.feedbackTextHtml;
          currentSelectedAnswerOptions = question.selectedAnswerOptions;

          return {
            ...question,
            loading: true,
          };
        });
      return null;
    });

    if (isPreview) {
      let selectedAnswerOptions;
      if (field === 'rating') {
        selectedAnswerOptions = Array.isArray(answer) ? answer : [answer];
      } else {
        selectedAnswerOptions = currentSelectedAnswerOptions;
      }
      const feedbackText = field === 'feedbackText' ? answer.plainText : currentFeedBackText;
      const feedbackTextHtml = field === 'feedbackText' ? answer.htmlText : currentFeedBackTextHtml;

      setQuestionList((questions) => {
        if (Array.isArray(questions))
          return questions.map((question) => {
            if (question.id !== questionId) return question;

            if (field === 'rating' || field === 'feedbackText') {
              return {
                ...question,
                loading: false,
                selectedAnswerOptions: Array.isArray(selectedAnswerOptions)
                  ? selectedAnswerOptions
                  : [selectedAnswerOptions],
                feedbackText,
                feedbackTextHtml,
              };
            }

            return question;
          });

        return null;
      });
    } else {
      let selectedAnswerOptions;
      if (field === 'rating') {
        selectedAnswerOptions = Array.isArray(answer) ? answer : [answer];
      } else {
        selectedAnswerOptions = currentSelectedAnswerOptions;
      }
      updateQuestionResponse({
        reviewId,
        employeeId,
        questionId,
        feedbackText: field === 'feedbackText' ? answer.plainText : currentFeedBackText,
        feedbackTextHtml: field === 'feedbackText' ? answer.htmlText : currentFeedBackTextHtml,
        selectedAnswerOptions,
        sourceForm: 'Overall form',
      })
        .then((res) => {
          const {
            id: quesId,
            // eslint-disable-next-line no-shadow
            selectedAnswerOptions,
            feedbackText,
            feedbackTextHtml,
          } = res?.question ?? {};
          setQNARating({
            averageQNARating: res.averageRating,
            maxQNARating: res.maxRating,
          });
          setQuestionList((questions) => {
            if (Array.isArray(questions))
              return questions.map((question) => {
                if (question.id !== quesId) return question;

                if (field === 'rating' || field === 'feedbackText') {
                  return {
                    ...question,
                    loading: false,
                    selectedAnswerOptions: Array.isArray(selectedAnswerOptions)
                      ? selectedAnswerOptions
                      : [selectedAnswerOptions],
                    feedbackText,
                    feedbackTextHtml,
                  };
                }

                return question;
              });

            return null;
          });
          dispatch(showInfoSnackbar('Form autosaved.'));
        })
        .catch((err) => {
          setQuestionList((questions) => {
            if (Array.isArray(questions))
              return questions.map((question) => {
                if (question.id !== questionId) return question;

                return {
                  ...question,
                  loading: false,
                };
              });

            return null;
          });

          dispatch(showErrorSnackbar(err.message || 'Some Error Occured while fetching Behavior Form'));
        });
    }
  }, []);

  const showLoader = () => (
    <DisplayInfo bgcolor={theme.palette.background.paper}>
      <SkeletonLoader skeletons={['100%', '100%']} />
    </DisplayInfo>
  );

  const showNoQuestionsMessage = () => (
    <DisplayInfo bgcolor={theme.palette.background.paper}>
      <StyledTypography variant="h6" color="textSecondaryLight" align="center" topGutter={4} bottomGutter={4}>
        {REVIEW_TEXTS.QA.NO_DATA}
      </StyledTypography>
    </DisplayInfo>
  );

  const showErrorMessage = () => (
    <DisplayInfo bgcolor={theme.palette.background.paper}>
      <StyledTypography variant="h6" color="red" align="center" topGutter={4} bottomGutter={4}>
        {REVIEW_TEXTS.QA.ERROR_MSG}
      </StyledTypography>
    </DisplayInfo>
  );

  useEffect(() => {
    setQuestionList(null);
    fetchData();
  }, [reviewId, employeeId, providerId, responseId, refetchForm]);

  return (
    <>
      {loading && showLoader()}
      {!loading && questionList === null && showErrorMessage()}
      {!loading &&
        questionList !== null &&
        (questionList.length === 0 ? (
          showNoQuestionsMessage()
        ) : (
          <>
            {!isSummaryView && (
              <StyledTypography variant="h6" bottomGutter={2}>
                Please answer the following questions
              </StyledTypography>
            )}
            <Box width="100%" bgcolor={theme.palette.background.paper}>
              <Grid container>
                <Grid item xs={isManagerView ? 8 : 12} style={{ borderRight: '1px solid #E1E1E1' }}>
                  {isPreview && (
                    <Grid item style={{ borderBottom: '1px solid #E1E1E1', padding: '16px' }}>
                      <Flex alignItems="center" gap="12px">
                        <StyledTypography wordBreak variant="body1" color="textPrimary">
                          View Template For:
                        </StyledTypography>
                        <RadioGroup onChange={setPreviewTemplateView} value={previewTemplateView}>
                          <Flex gap="12px">
                            <Radio value="SELF" isChecked={previewTemplateView === 'SELF'}>
                              Self Review
                            </Radio>
                            <Radio value="MANAGER" isChecked={previewTemplateView === 'MANAGER'}>
                              Manager Review
                            </Radio>
                            <Radio value="PEER" isChecked={previewTemplateView === 'PEER'}>
                              Peer Review
                            </Radio>
                            <Radio value="UPWARD" isChecked={previewTemplateView === 'UPWARD'}>
                              Upward Review
                            </Radio>
                            <Radio value="DLM" isChecked={previewTemplateView === 'DLM'}>
                              Dotted Line Manager Review
                            </Radio>
                          </Flex>
                        </RadioGroup>
                      </Flex>
                    </Grid>
                  )}
                  <Box borderRight={isManagerView ? `1px solid ${theme.palette.borders.lightGrey}` : 'none'}>
                    {questionList.map((question, index) => (
                      <QAComponent
                        key={question.id}
                        question={question}
                        questionNo={index + 1}
                        submitAnswer={answerQuestion}
                        isSubmitClicked={isSubmitClicked}
                        isManagerView={isManagerView}
                        isPreview={isPreview}
                        isViewOnlyModeEnabled={question.isViewOnlyModeEnabled}
                        previewTemplateView={previewTemplateView}
                      />
                    ))}
                  </Box>
                </Grid>
                {isManagerView && (
                  <StickyGridContainer item xs={4}>
                    <PeerFeedbackComp reviewId={reviewId} employeeId={employeeId} />
                  </StickyGridContainer>
                )}
                {isShowQNARating && (
                  <RatingContainer container padding="1rem">
                    <StyledGrid item sm={3}>
                      <StyledTypography color="textSecondary">Overall questions score</StyledTypography>
                    </StyledGrid>
                    <StyledGrid item sm={3}>
                      <StyledTypography>
                        {qnaRating.averageQNARating !== undefined ? qnaRating.averageQNARating.toFixed(2) : 'NA'}
                      </StyledTypography>
                    </StyledGrid>
                  </RatingContainer>
                )}
              </Grid>
            </Box>
          </>
        ))}
    </>
  );
};

export default QAForm;
